<template>
  <!-- 
    REFACTOR OPPORTUNITY:
    This component is too large (765 lines) and should be broken down into smaller components:
    1. Create a FormField.vue component for input fields with validation
    2. Create separate components for different insurance types
    3. Extract validation logic to a separate utility
    4. Create a FormSubmission.vue component for the submission handling
    5. Move styles to separate CSS files
  -->
  <DefaultLayout>
    <div v-if="!isSuccess" class="container my-8 px-6 mx-auto">
      <div
        class="my-8 md:my-24 w-full md:w-1/3 h-auto bg-white shadow-lg mx-auto rounded-lg mb-10 p-6 flex flex-col items-center space-y-4"
      >
        <img 
          :src="imagePath" 
          class="w-24" 
          alt="Insurance Type" 
          @error="e => e.target.src = defaultIcon" 
        />
        <h2 class="text-2xl font-bold text-gray-800">{{ productType }} Quote</h2>

        <div class="w-full max-w-lg">
          <label for="name" class="block text-sm font-medium text-gray-700"
            >First Name <span class="text-red-500">*</span></label
          >
          <InputText
            id="name"
            v-model="name"
            type="text"
            placeholder="Your Name"
            class="mt-1 w-full border border-gray-300 px-3 py-2 rounded-md shadow-sm"
            :class="{ 'border-red-500': nameError }"
          />

          <label
            for="lastname"
            class="block mt-4 text-sm font-medium text-gray-700"
            >Last Name <span class="text-red-500">*</span></label
          >
          <InputText
            id="lastname"
            v-model="lastName"
            type="text"
            placeholder="Your Last Name"
            class="mt-1 w-full border border-gray-300 px-3 py-2 rounded-md shadow-sm"
            :class="{ 'border-red-500': lastNameError }"
          />

          <label for="email" class="block mt-4 text-sm font-medium text-gray-700"
            >Your Email <span class="text-red-500">*</span></label
          >
          <InputText
            id="email"
            v-model="email"
            type="email"
            placeholder="Your Email"
            class="mt-1 w-full border border-gray-300 px-3 py-2 rounded-md shadow-sm"
            :class="{ 'border-red-500': emailError }"
          />

          <label for="phone" class="block mt-4 text-sm font-medium text-gray-700"
            >Your Phone Number <span class="text-red-500">*</span></label
          >
          <InputMask
            id="phone"
            v-model="phone"
            type="tel"
            placeholder="Your Phone Number"
            mask="(999) 999-9999"
            class="mt-1 w-full border border-gray-300 px-3 py-2 rounded-md shadow-sm"
            :class="{ 'border-red-500': phoneError }"
          />

          <label
            v-if="
              productType === 'Home Insurance' ||
              productType === 'Flood Insurance' ||
              productType === 'Auto Insurance'
            "
            for="property_address"
            class="block mt-4 text-sm font-medium text-gray-700"
            >Property Address <span class="text-red-500">*</span></label
          >
          <InputText
            v-if="
              productType === 'Home Insurance' ||
              productType === 'Flood Insurance' ||
              productType === 'Auto Insurance'
            "
            id="property_address"
            v-model="propertyAddress"
            placeholder="Property Address"
            class="mt-1 w-full border border-gray-300 px-3 py-2 rounded-md shadow-sm"
            :class="{ 'border-red-500': propertyAddressError }"
            @blur="validatePropertyAddress"
          />

          <label
            for="message"
            class="block mt-4 text-sm font-medium text-gray-700"
          >
            Your Message
          </label>
          <Textarea
            id="message"
            v-model="message"
            rows="5"
            placeholder="Your Message"
            class="mt-1 w-full border border-gray-300 px-3 py-2 rounded-md shadow-sm"
          />
        </div>
        <label for="insured" class="w-full max-w-lg">
          Are you currently insured with Insurance Express and Member Companies?
          <span class="required">* </span>
          <span class="help-icon">
            <span class="question-mark">?</span>
            <span class="help-content">
              <span
                class="text-xs text-blue-500 font-bold"
                style="color: #0b2779"
              >
                Member Companies<br />
                <span class="text-xs text-gray-500">
                  GLV Insurance<br />
                  K&G Insurance<br />
                  QuoteSlash<br />
                  RocketFlood<br />
                  RocketMGA<br />
                  Nubuild<br />
                  TheLeadExchange<br />
                  Protego VIP<br />
                  Str8line Group<br />
                  Str8line Enterprises
                </span>
              </span>
            </span>
          </span>
        </label>

        <div
          class="flex justify-start pt-3"
          :class="{ 'text-red-500': insuredError }"
        >
          <div class="flex items-center mr-4">
            <input
              id="insuredYes"
              v-model="insured"
              type="radio"
              value="Yes"
              class="form-radio h-5 w-5 text-blue-600"
            />
            <label for="insuredYes" class="ml-2">Yes</label>
          </div>
          <div class="flex items-center">
            <input
              id="insuredNo"
              v-model="insured"
              type="radio"
              value="No"
              class="form-radio h-5 w-5 text-blue-600"
            />
            <label for="insuredNo" class="ml-2">No</label>
          </div>
        </div>

        <Button
          label="Submit"
          class="mt-6 w-full max-w-lg bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
          @click="submitForm"
        />

        <p class="note">
          Fields marked with <span class="required">*</span> are required.
        </p>

        <Message
          v-if="phoneError"
          class="fixed top-20 right-2.5 z-50 bg-red-500 text-white p-4 rounded-md shadow-lg"
        >
          Phone is Required
        </Message>
        <Message
          v-if="emailError"
          class="fixed top-20 right-2.5 z-50 bg-red-500 text-white p-4 rounded-md shadow-lg"
        >
          Email is Required
        </Message>
        <Message
          v-if="nameError"
          class="fixed top-20 right-2.5 z-50 bg-red-500 text-white p-4 rounded-md shadow-lg"
        >
          Name is Required
        </Message>
        <Message
          v-if="lastNameError"
          class="fixed top-20 right-2.5 z-50 bg-red-500 text-white p-4 rounded-md shadow-lg"
        >
          Last Name is Required
        </Message>
        <Message
          v-if="insuredError"
          class="fixed top-20 right-2.5 z-50 bg-red-500 text-white p-4 rounded-md shadow-lg"
        >
          Please select if you are currently insured
        </Message>
        <Message
          v-if="propertyAddressError"
          class="fixed top-20 right-2.5 z-50 bg-red-500 text-white p-4 rounded-md shadow-lg"
        >
          Property Address is Required
        </Message>
      </div>
    </div>
    
    <div v-else class="container mx-auto px-6 py-16">
      <div class="max-w-2xl mx-auto text-center bg-white rounded-lg shadow-lg p-8">
        <!-- Success Icon -->
        <div class="mb-8">
          <div class="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg class="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
            </svg>
          </div>
        </div>

        <!-- Success Heading -->
        <h1 class="text-4xl font-bold text-gray-900 mb-4">
          Thank You!
        </h1>
        
        <h2 class="text-xl text-gray-600 mb-8">
          Your {{ productType }} quote request has been received
        </h2>

        <!-- Success Message -->
        <div class="bg-green-50 border border-green-200 rounded-lg p-6 mb-8">
          <p class="text-lg text-gray-700 leading-relaxed">
            We've received your request and one of our insurance experts will contact you within 
            <span class="font-semibold text-green-700">24 hours</span> to discuss your 
            {{ productType.toLowerCase() }} options and provide you with a personalized quote.
          </p>
        </div>

        <!-- Next Steps -->
        <div class="text-left bg-gray-50 rounded-lg p-6 mb-8">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">What happens next?</h3>
          <ul class="space-y-3 text-gray-700">
            <li class="flex items-start">
              <span class="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-semibold mr-3 mt-0.5">1</span>
              <span>Our insurance expert will review your request</span>
            </li>
            <li class="flex items-start">
              <span class="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-semibold mr-3 mt-0.5">2</span>
              <span>We'll call you to discuss your specific needs</span>
            </li>
            <li class="flex items-start">
              <span class="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-semibold mr-3 mt-0.5">3</span>
              <span>You'll receive personalized quotes from top-rated insurers</span>
            </li>
          </ul>
        </div>

        <!-- Contact Info -->
        <div class="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
          <p class="text-sm text-blue-800">
            <strong>Need immediate assistance?</strong><br>
            Call us at <a href="tel:+1-800-123-4567" class="font-semibold underline">(800) 123-4567</a> 
            or email <a href="mailto:quotes@insuranceexpress.com" class="font-semibold underline">quotes@insuranceexpress.com</a>
          </p>
        </div>

        <!-- Action Buttons -->
        <div class="flex flex-col sm:flex-row gap-4 justify-center">
          <a
            href="/"
            class="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors duration-200"
          >
            Back to Homepage
          </a>
          <a
            href="/quote"
            class="inline-flex items-center justify-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200"
          >
            Get Another Quote
          </a>
        </div>
      </div>
    </div>
  </DefaultLayout>
</template>

<script>
import { ref, onMounted, nextTick, watch } from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';
import DefaultLayout from '@/layouts/DefaultLayout.vue';
import { InputText, Textarea, Button, InputMask, Message } from '@/components/ui';
import { useNotification } from '@kyvg/vue3-notification';
import confetti from 'canvas-confetti';

// Direct imports for icons
import homeIcon from '@/assets/images/products/icons/home/icon-home-main.svg';
import autoIcon from '@/assets/images/products/icons/auto/icon-auto-main.svg';
import floodIcon from '@/assets/images/products/icons/flood/icon-flood-main.svg';
import medicareIcon from '@/assets/images/products/icons/medicare.svg';
import lifeIcon from '@/assets/images/products/icons/life/icon-life-main.svg';
import petIcon from '@/assets/images/products/icons/pet/icon-pet-main.svg';
import businessIcon from '@/assets/images/products/icons/business/icon-business-main.svg';
import defaultIcon from '@/assets/images/products/icons/default-icon.svg';

// Helper function to get icon path
const getIconPath = (iconName) => {
  switch (iconName) {
    case 'home':
      return homeIcon;
    case 'auto':
      return autoIcon;
    case 'flood':
      return floodIcon;
    case 'medicare':
      return medicareIcon;
    case 'life':
      return lifeIcon;
    case 'pet':
      return petIcon;
    case 'business':
      return businessIcon;
    default:
      return defaultIcon;
  }
};

export default {
  components: {
    DefaultLayout,
    InputText,
    Textarea,
    Button,
    InputMask,
    Message,
  },
  props: ['product'],
  setup() {
    const name = ref('');
    const lastName = ref('');
    const email = ref('');
    const phone = ref('');
    const message = ref('');
    const productType = ref('');
    const propertyAddress = ref('');
    const imagePath = ref(''); // Initialize with an empty string or default image path
    watch(productType, (newValue) => {
      switch (newValue) {
        case 'Home Insurance':
          imagePath.value = getIconPath('home');
          break;
        case 'Auto Insurance':
          imagePath.value = getIconPath('auto');
          break;
        case 'Flood Insurance':
          imagePath.value = getIconPath('flood');
          break;
        case 'Medicare':
          imagePath.value = getIconPath('medicare');
          break;
        case 'Life Insurance':
          imagePath.value = getIconPath('life');
          break;
        case 'Pet Insurance':
          imagePath.value = getIconPath('pet');
          break;
        case 'Business Insurance':
          imagePath.value = getIconPath('business');
          break;
        default:
          imagePath.value = defaultIcon;
          break;
      }
    });
    const nameError = ref(false);
    const lastNameError = ref(false);
    const emailError = ref(false);
    const phoneError = ref(false);
    const propertyAddressError = ref(false);
    const insured = ref('');

    const insuredOptions = ref(['Yes', 'No']);

    const insuredError = ref(false);

    const validateInsured = () => {
      insuredError.value = !insured.value || insured.value.trim() === '';
    };
    const notification = useNotification();
    const isSuccess = ref(false);
    const validateName = () => {
      nameError.value = name.value.trim() === '';
    };
    const validateLastName = () => {
      lastNameError.value = name.value.trim() === '';
    };

    const validateEmail = () => {
      emailError.value = !/^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,7}$/.test(
        email.value,
      );
    };

    const validatePhone = () => {
      // Retrieve the raw phone number value without the mask
      const rawPhoneValue = phone.value.replace(/\D/g, '');

      // Perform validation on the raw phone number value
      phoneError.value = !/^\+?[1-9]\d{1,14}$/.test(rawPhoneValue);
    };
    const validatePropertyAddress = () => {
      console.log('🔍 [VALIDATION] validatePropertyAddress called');
      console.log('🔍 [VALIDATION] productType.value:', productType.value);
      console.log('🔍 [VALIDATION] propertyAddress.value:', JSON.stringify(propertyAddress.value));
      console.log('🔍 [VALIDATION] propertyAddress.value.length:', propertyAddress.value?.length);
      console.log('🔍 [VALIDATION] propertyAddress.value.trim():', JSON.stringify(propertyAddress.value?.trim?.()));
      console.log('🔍 [VALIDATION] propertyAddress.value.trim() === "":', propertyAddress.value?.trim?.() === '');
      
      if (['Home Insurance', 'Flood Insurance', 'Auto Insurance'].includes(productType.value)) {
        const isRequired = true;
        const isEmptyOrNull = !propertyAddress.value || propertyAddress.value.trim() === '';
        
        console.log('🔍 [VALIDATION] Property address is REQUIRED for this product type');
        console.log('🔍 [VALIDATION] isEmptyOrNull:', isEmptyOrNull);
        
        propertyAddressError.value = isEmptyOrNull;
        
        console.log('🔍 [VALIDATION] propertyAddressError.value set to:', propertyAddressError.value);
      } else {
        console.log('🔍 [VALIDATION] Property address is NOT required for this product type');
        propertyAddressError.value = false;
      }
      
      console.log('🔍 [VALIDATION] Final propertyAddressError.value:', propertyAddressError.value);
    };

    const submitForm = () => {
      console.log('📝 [SUBMIT] ==================== FORM SUBMISSION STARTED ====================');
      console.log('📝 [SUBMIT] All form values at submission time:');
      console.log('📝 [SUBMIT] name:', JSON.stringify(name.value));
      console.log('📝 [SUBMIT] lastName:', JSON.stringify(lastName.value));
      console.log('📝 [SUBMIT] email:', JSON.stringify(email.value));
      console.log('📝 [SUBMIT] phone:', JSON.stringify(phone.value));
      console.log('📝 [SUBMIT] propertyAddress:', JSON.stringify(propertyAddress.value));
      console.log('📝 [SUBMIT] productType:', JSON.stringify(productType.value));
      console.log('📝 [SUBMIT] insured:', JSON.stringify(insured.value));
      
      console.log('📝 [SUBMIT] Running validation functions...');
      
      validateName();
      console.log('📝 [SUBMIT] After validateName(), nameError:', nameError.value);
      
      validateLastName();
      console.log('📝 [SUBMIT] After validateLastName(), lastNameError:', lastNameError.value);
      
      validateEmail();
      console.log('📝 [SUBMIT] After validateEmail(), emailError:', emailError.value);
      
      validatePhone();
      console.log('📝 [SUBMIT] After validatePhone(), phoneError:', phoneError.value);
      
      validateInsured();
      console.log('📝 [SUBMIT] After validateInsured(), insuredError:', insuredError.value);
      
      validatePropertyAddress();
      console.log('📝 [SUBMIT] After validatePropertyAddress(), propertyAddressError:', propertyAddressError.value);
      
      console.log('📝 [SUBMIT] ==================== VALIDATION SUMMARY ====================');
      console.log('📝 [SUBMIT] nameError:', nameError.value);
      console.log('📝 [SUBMIT] lastNameError:', lastNameError.value);
      console.log('📝 [SUBMIT] emailError:', emailError.value);
      console.log('📝 [SUBMIT] phoneError:', phoneError.value);
      console.log('📝 [SUBMIT] insuredError:', insuredError.value);
      console.log('📝 [SUBMIT] propertyAddressError:', propertyAddressError.value);
      
      const hasErrors = nameError.value || lastNameError.value || emailError.value || phoneError.value || insuredError.value || propertyAddressError.value;
      console.log('📝 [SUBMIT] hasErrors:', hasErrors);
      
      if (
        !nameError.value &&
        !lastNameError.value &&
        !emailError.value &&
        !phoneError.value &&
        !insuredError.value &&
        !propertyAddressError.value
      ) {
        console.log('📝 [SUBMIT] ✅ All validations passed! Proceeding with form submission...');
        
        const templateParams = {
          name: name.value,
          lastName: lastName.value,
          email: email.value,
          phone: phone.value,
          propertyAddress: propertyAddress.value,
          message: message.value,
          productType: productType.value,
          insured: insured.value,
        };
        
        console.log('📝 [SUBMIT] templateParams:', JSON.stringify(templateParams, null, 2));

        // Send to our Laravel backend instead of EmailJS + Jangl API
        axios.post('/api/submit-quote', templateParams)
          .then(function (response) {
            console.log('✅ [SUBMIT] Quote submitted successfully:', response.data);
            isSuccess.value = true;
            notification.notify({
              id: Date.now(),
              title: 'Success',
              text: response.data.message || 'Your quote request has been submitted successfully!',
              type: 'success',
            });

            // Show confetti
            confetti({
              particleCount: 100,
              spread: 70,
              origin: { x: 0.5, y: 0.5 },
            });
          })
          .catch(function (error) {
            console.error('❌ [SUBMIT] Failed to submit quote:', error);
            
            let errorMessage = 'Failed to submit quote request. Please try again or contact us directly.';
            
            if (error.response && error.response.data && error.response.data.message) {
              errorMessage = error.response.data.message;
            }
            
            notification.notify({
              id: Date.now(),
              title: 'Error',
              text: errorMessage,
              type: 'error',
            });
          });
      }
    };

    // Accessing the router instance
    const router = useRouter();

    onMounted(() => {
      // Scroll to the top of the page
      window.scrollTo({ top: 0, behavior: 'smooth' });

      // Retrieving the product type from the query parameter
      const query = router.currentRoute.value.query;
      if (query.productType) {
        productType.value = query.productType;
      }
    });

    return {
      name,
      lastName,
      email,
      phone,
      propertyAddress,
      message,
      productType,
      insured,
      insuredOptions,
      nameError,
      lastNameError,
      emailError,
      phoneError,
      insuredError,
      propertyAddressError,
      validatePropertyAddress,
      validateName,
      validateLastName,
      validateEmail,
      validatePhone,
      validateInsured,
      submitForm,
      isSuccess,
      imagePath,
    };
  },
};
</script>

<style scoped>
:deep(.p-button) {
  background: #0b2779;
}
:deep(.p-button:hover) {
  background: #6c7dae;
}
.active-insured {
  background-color: #0b2779; /* this is a dark blue color */
  color: #ffffff; /* white text */
}

.error {
  border: 2px solid #ff0000; /* red border */
  color: #ff0000; /* red text */
}
.error-border input[type='radio'] {
  border-color: #ff0000; /* red border */
}
.error-container {
  border: 2px solid red;
  border-radius: 4px;
  padding: 4px;
}
.help-icon {
  position: relative;
  display: inline-flex;
  align-items: center; /* Adjust the vertical alignment */
}

.question-mark {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  background-color: #333333;
  color: #fff;
  border-radius: 50%;
  cursor: pointer;
  font-size: 12px;
  line-height: 1;
  transition: transform 0.2s ease-in-out;
}

.question-mark:hover {
  transform: scale(1.1); /* Apply the desired animation effect */
}

.help-content {
  position: absolute;
  z-index: 1;
  top: calc(100% + 4px); /* Adjust the top positioning */
  left: 0;
  display: none;
  width: max-content;
  max-width: 250px;
  padding: 8px;
  background-color: #fff;
  border: 1px solid #999;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  white-space: normal;
  word-wrap: break-word;
  text-align: left;
}

.help-text {
  display: block;
  color: #0b2779;
  font-size: 12px;
  line-height: 1.2;
  margin-bottom: 8px;
}

.help-icon:hover .help-content {
  display: block;
}
</style>
